import type { PaginatedNode } from "./PaginatedNode";
import type { BranchProjectionCtx } from "./types";

// Re-export for back-compat with existing imports of this module.
export type { BranchProjectionCtx } from "./types";

/**
 * One entry per child slot in `collectFilteredBranchItems`. Pre-flattens
 * `node.pages.pages[*].items[*]` into an indexable array so the forward
 * and backward chain passes can iterate by absolute index without
 * re-walking the page structure.
 */
interface BranchEntry {
  readonly item: unknown;
  readonly childNode: PaginatedNode;
  readonly isCollapsed: boolean;
  readonly hasPages: boolean;
  /**
   * `true` while the controller is fetching this node's first page.
   * Loading empty entries are exposed (header rendered as a placeholder)
   * AND keep the chain progressing — siblings stay visible while we
   * wait for records, so the consumer's layout doesn't shrink and the
   * browser doesn't clamp `scrollTop`. Cleared the moment the fetch
   * resolves (applied / skipped / aborted) — see
   * `PaginationController.executeFetch`.
   */
  readonly loading: boolean;
}

/**
 * Builds the projection for `node`'s children and appends to `out`.
 *
 * Two independent chain passes are computed and then combined:
 *   - Forward chain (`fillForwardExposed`) walks document order and
 *     gates the scroll-down boundary. It exposes at most one bootstrap
 *     slot AFTER the loaded region.
 *   - Backward chain (`fillBackwardExposed`) walks reverse order and
 *     gates the scroll-up boundary. It exposes at most one `fromLast`
 *     slot BEFORE the loaded region.
 *
 * Combine rule (`combineExposure`):
 *   - Loaded non-collapsed entries are exposed only when BOTH chains
 *     reach them — a loading sibling on either side keeps its
 *     neighbours hidden.
 *   - Collapsed entries (with or without pages) and empty non-collapsed
 *     entries (terminal-stuck, bootstrap, fromLast) are exposed when
 *     EITHER chain reaches them. Collapsed entries don't render
 *     content, only the header — so the AND-rule's anti-leak guarantee
 *     doesn't apply.
 */
export function collectFilteredBranchItems(
  node: PaginatedNode,
  out: unknown[],
  ctx: BranchProjectionCtx,
): void {
  if (node.depth >= ctx.maxDepth) {
    return;
  }

  const entries = buildBranchEntries(node, ctx);
  if (entries.length === 0) {
    return;
  }

  const boundaries = findLoadedBoundaries(entries);
  const forwardExposed = new Array<boolean>(entries.length).fill(false);
  const backwardExposed = new Array<boolean>(entries.length).fill(false);

  fillForwardExposed(entries, forwardExposed, boundaries.firstLoadedIdx, ctx);
  fillBackwardExposed(entries, backwardExposed, boundaries.lastLoadedIdx, ctx);

  const exposed = combineExposure(entries, forwardExposed, backwardExposed);

  emitExposedEntries(entries, exposed, out, ctx);
}

/**
 * Recursive "is everything under this node loaded on the after side?"
 * check used by the forward (scroll-down) chain. Also re-used by the
 * controller's `tryProgressBoundary` to gate eager boundary bootstrap.
 */
export function isSubtreeCompleteDown(node: PaginatedNode, ctx: BranchProjectionCtx): boolean {
  return isSubtreeComplete(node, "after", ctx);
}

/**
 * Mirror of `isSubtreeCompleteDown` for the backward (scroll-up) chain:
 * "is everything under this node loaded on the before side?".
 *
 * Empty descendants count as incomplete — they are fromLast or
 * bootstrap candidates, so the chain must block on them.
 */
export function isSubtreeCompleteUp(node: PaginatedNode, ctx: BranchProjectionCtx): boolean {
  return isSubtreeComplete(node, "before", ctx);
}

function buildBranchEntries(node: PaginatedNode, ctx: BranchProjectionCtx): BranchEntry[] {
  const entries: BranchEntry[] = [];
  for (const page of node.pages.pages) {
    for (let i = 0; i < page.items.length; i++) {
      const childId = page.itemIds[i];
      const childNode = node.getChild(childId);
      if (!childNode) {
        continue;
      }
      entries.push({
        item: page.items[i],
        childNode,
        isCollapsed: ctx.collapsedIds.has(childId),
        hasPages: !childNode.pages.isEmpty,
        loading: childNode.loading,
      });
    }
  }
  return entries;
}

/**
 * First and last index of entries that contribute to the visible loaded
 * region. Skips collapsed entries — their records are dormant (header
 * only), so using them as the chain anchor would mis-classify adjacent
 * empty slots as bootstrap targets.
 */
function findLoadedBoundaries(entries: readonly BranchEntry[]): {
  firstLoadedIdx: number;
  lastLoadedIdx: number;
} {
  let firstLoadedIdx = -1;
  let lastLoadedIdx = -1;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (!entry.hasPages || entry.isCollapsed) {
      continue;
    }
    if (firstLoadedIdx === -1) {
      firstLoadedIdx = i;
    }
    lastLoadedIdx = i;
  }
  return { firstLoadedIdx, lastLoadedIdx };
}

function combineExposure(
  entries: readonly BranchEntry[],
  forwardExposed: readonly boolean[],
  backwardExposed: readonly boolean[],
): readonly boolean[] {
  const exposed = new Array<boolean>(entries.length).fill(false);
  for (let i = 0; i < entries.length; i++) {
    const reachedByForward = forwardExposed[i];
    const reachedByBackward = backwardExposed[i];
    const requiresBothChains = entries[i].hasPages && !entries[i].isCollapsed;
    exposed[i] = requiresBothChains
      ? reachedByForward && reachedByBackward
      : reachedByForward || reachedByBackward;
  }
  return exposed;
}

/**
 * Forward (scroll-down) chain. Walks left→right, exposes loaded
 * children whose subtree is complete, stops at the first non-collapsed
 * empty bootstrap slot. Empties strictly above the loaded region are
 * skipped silently — they belong to the backward chain. The chain stop
 * on an incomplete loaded sibling is intentional: it caps the rendered
 * list so `walkNode` does not fire after-loads against everything at once
 * (descent-first backpressure).
 *
 * Loading-empty entries are a special case: when the controller is
 * actively fetching the first page for an empty node (most often a
 * just-expanded subgroup), we expose the header AND keep the chain
 * progressing. The siblings below stay visible while records are in
 * transit — without this rule the consumer's projection would shrink
 * and the browser would clamp `scrollTop` to 0 before records arrive.
 */
function fillForwardExposed(
  entries: readonly BranchEntry[],
  exposed: boolean[],
  firstLoadedIdx: number,
  ctx: BranchProjectionCtx,
): void {
  let chainComplete = true;
  for (let i = 0; i < entries.length && chainComplete; i++) {
    const entry = entries[i];

    if (entry.hasPages) {
      exposed[i] = true;
      chainComplete = entry.isCollapsed || isSubtreeCompleteDown(entry.childNode, ctx);
      continue;
    }

    if (entry.isCollapsed) {
      exposed[i] = true;
      continue;
    }

    // Empty actively-loading entry — expose AND keep chain alive so
    // siblings below stay visible. See comment on `BranchEntry.loading`.
    if (entry.loading) {
      exposed[i] = true;
      continue;
    }

    // Empty above the loaded region — backward chain owns this fromLast slot.
    // Must precede the canLoad("after") branch: top-trimmed empties carry a
    // sticky hasMore=false overlay but are still valid fromLast targets.
    if (firstLoadedIdx >= 0 && i < firstLoadedIdx) {
      continue;
    }

    // Forward-exhausted empty — expose header so the chain advances past it.
    if (!entry.childNode.pages.canLoad("after")) {
      exposed[i] = true;
      continue;
    }

    // Forward bootstrap slot. Exactly one per parent per cycle.
    exposed[i] = true;
    return;
  }
}

/**
 * Backward (scroll-up) chain. Mirror of `fillForwardExposed`: walks
 * right→left, exposes loaded children whose before-side subtree is
 * complete, stops at the first non-collapsed empty `fromLast` slot.
 * Empties strictly below the loaded region (and the entire span when
 * there is no loaded region at all) are skipped silently — they
 * belong to the forward chain. Loading-empty entries are kept visible
 * and do not break the chain (see `fillForwardExposed`).
 */
function fillBackwardExposed(
  entries: readonly BranchEntry[],
  exposed: boolean[],
  lastLoadedIdx: number,
  ctx: BranchProjectionCtx,
): void {
  if (lastLoadedIdx === -1) {
    return;
  }

  let chainComplete = true;
  for (let i = entries.length - 1; i >= 0 && chainComplete; i--) {
    const entry = entries[i];

    if (entry.hasPages) {
      exposed[i] = true;
      chainComplete = entry.isCollapsed || isSubtreeCompleteUp(entry.childNode, ctx);
      continue;
    }

    if (entry.isCollapsed) {
      exposed[i] = true;
      continue;
    }

    // Empty actively-loading entry — expose AND keep chain alive.
    if (entry.loading) {
      exposed[i] = true;
      continue;
    }

    // Empty below the loaded region — forward chain owns any bootstrap slot here.
    if (i > lastLoadedIdx) {
      continue;
    }

    // Backward-exhausted empty — expose header so the chain advances past it.
    if (!entry.childNode.pages.canLoad("before")) {
      exposed[i] = true;
      continue;
    }

    // Backward `fromLast` slot. Exactly one per parent per cycle.
    exposed[i] = true;
    return;
  }
}

function emitExposedEntries(
  entries: readonly BranchEntry[],
  exposed: readonly boolean[],
  out: unknown[],
  ctx: BranchProjectionCtx,
): void {
  for (let i = 0; i < entries.length; i++) {
    if (!exposed[i]) {
      continue;
    }
    const entry = entries[i];
    out.push(entry.item);
    if (entry.hasPages && !entry.isCollapsed) {
      collectFilteredBranchItems(entry.childNode, out, ctx);
    }
  }
}

/**
 * Recursive completeness check shared by the forward and backward chains:
 * `node` (and every non-collapsed descendant) is fully loaded on `direction`.
 * Used to track `chainComplete` for the bootstrap-slot decision in
 * `fillForwardExposed` / `fillBackwardExposed`.
 *
 * Asymmetric rules between directions:
 *   - Forward: blocks on `canLoad("after")=true` AND on any descendant
 *     subtree that itself reports incomplete on the after side. The
 *     forward bootstrap will eventually reach every such node as the
 *     user scrolls down.
 *   - Backward: blocks on `canLoad("before")=true` ONLY when
 *     `node.pages.wasScrolledUp` is true (i.e. the user actually pulled
 *     records from above through a `fromLast` probe or an explicit
 *     `before` fetch). Same gating applies to the "empty descendant
 *     blocks" rule — an empty descendant is only a fromLast candidate
 *     when its parent's user-driven scroll history already includes a
 *     before-pull. A forward-bootstrapped parent with empty trailing
 *     children treats them as forward-bootstrap targets, not fromLast
 *     targets, so they do not block the before side.
 */
function isSubtreeComplete(
  node: PaginatedNode,
  direction: "after" | "before",
  ctx: BranchProjectionCtx,
): boolean {
  if (isOwnLoadIncomplete(node, direction)) {
    return false;
  }
  // hasNextLevel(depth) === depth + 1 < config.length === depth < maxDepth.
  if (node.depth >= ctx.maxDepth) {
    return true;
  }

  // Backward chain blocks on an empty descendant only when the user
  // has actually scrolled up through this node — that is the user-intent
  // signal that earlier records are imminent. A forward-only parent
  // treats empty descendants as forward-bootstrap candidates that don't
  // gate the before side.
  const blockOnEmptyChild = direction === "before" && node.pages.wasScrolledUp;
  for (const page of node.pages.pages) {
    for (const childId of page.itemIds) {
      if (ctx.collapsedIds.has(childId)) {
        continue;
      }
      const child = node.getChild(childId);
      if (!child) {
        return false;
      }
      if (child.pages.isEmpty) {
        // Fresh-never-fetched descendant. The forward direction must NOT
        // gate on these — they are pending-bootstrap placeholders, and
        // the chain logic at the parent level will expose them as
        // bootstrap slots when reached. Blocking here drops loaded
        // sibling subtrees of an ancestor that just gained a new
        // empty child via a reload (e.g. WS-driven group composition
        // change) — exactly the visible flicker users observe on slow
        // networks. The backward direction continues to honour the
        // `wasScrolledUp` gate so explicit scroll-up `fromLast` flows
        // remain correct.
        if (blockOnEmptyChild) {
          return false;
        }
        continue;
      }
      if (!isSubtreeComplete(child, direction, ctx)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Per-direction "this node still wants to load more on its own side".
 * The forward and backward checks are deliberately asymmetric — see
 * the docstring on `isSubtreeComplete` for the rationale.
 */
function isOwnLoadIncomplete(node: PaginatedNode, direction: "after" | "before"): boolean {
  if (direction === "after") {
    return node.pages.canLoad("after");
  }
  return node.pages.canLoad("before") && node.pages.wasScrolledUp;
}
